<template>
    <v-form @submit.prevent="upsertTimeOff" ref="form">
        <v-card dense class="rounded-4">

            <!-- HEADER -->
            <v-toolbar flat height="45px">

                <span class="font-weight-bold">
                    {{ timeOff.mode == 'Add'
                        ? 'Add Personal Time Off'
                        : 'Edit Personal Time Off'
                    }}
                </span>

                <v-spacer></v-spacer>

                <help-link
                    appId="attendance"
                    :pageId="enableDateRange ? 'timeoff-page' : 'timeoff-daily'"
                    color="black"
                />

                <v-btn icon @click.native="closeDialog">
                    <v-icon>close</v-icon>
                </v-btn>

            </v-toolbar>

            <!-- ERROR -->
            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="rounded-4">
                Error encountered.
                {{ errorMessage }}
            </v-alert>

            <!-- APPROVED -->
            <v-alert v-if="isApproved" :value="true" color="warning" icon="warning" outline class="mt-8 rounded-4">
                This is already approved.
                Clicking Save will update back to For Review.
            </v-alert>

            <!-- INVALID HOURS -->
            <v-alert v-if="!validInputHours" :value="true" color="error" icon="error" outline class="mt-8 mr-10 ml-10 rounded-4">
                Requested hours exceeds available balance.
            </v-alert>

            <v-card-text dense>
                <v-layout row wrap>
                    <v-flex xs12 class="form-content">

                        <!-- Request for Employee -->
                        <v-layout v-if="isRequestForOtherUser  && mode == 'Add'" row wrap>
                            <v-flex xs12 sm12 class="form-row">
                                <v-autocomplete
                                    v-model="requestForUserId"
                                    :disabled="disabled"
                                    :items="team_members"
                                    :loading="loading_team_members"
                                    :rules="[rules.required, rules.timesheetNotApproved]"
                                    chips
                                    color="primary lighten-2"
                                    label="Request for Employee"
                                    item-text="name"
                                    item-value="userId"
                                    @change="getAvailableBalanceBySelectedUser"
                                    >
                                    <template v-slot:selection="data">
                                        <v-chip :selected="data.selected" class="chip--select-multi ml-0">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="data.item.avatar"></v-img>
                                            </v-avatar>
                                            {{ data.item.name }}
                                        </v-chip>
                                    </template>
                                    <template v-slot:item="data">
                                        <template v-if="typeof data.item !== 'object'">
                                            <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                        </template>
                                        <template v-else>
                                        <v-list-tile-avatar>
                                            <img :src="data.item.avatar">
                                        </v-list-tile-avatar>
                                        <v-list-tile-content>
                                            <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                            <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                        </v-list-tile-content>
                                        </template>
                                    </template>
                                </v-autocomplete>
                            </v-flex>
                        </v-layout>
                        
                        <!-- DATE -->
                        <v-layout row wrap>

                            <!-- SINGLE DATE -->
                            <template v-if="!enableDateRange">
                                <v-flex xs12>
                                    <v-text-field label="Date" v-model="transDate" disabled/>
                                </v-flex>
                            </template>

                            <!-- DATE RANGE -->
                            <template v-else>
                                <v-flex xs12 sm6 :class="isMobile?'':'pr-15'">
                                    <v-menu
                                        v-model="menuTransDate"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >

                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                label="Start Date"
                                                v-model="transDate"
                                                readonly
                                                v-on="on"
                                                :rules="[rules.required]"
                                                :disabled="disabled"
                                            />
                                        </template>

                                        <v-date-picker v-model="transDate" :allowed-dates="allowedDates"/>

                                    </v-menu>
                                </v-flex>

                                <v-flex xs12 sm6 :class="isMobile?'':'pl-15'">
                                    <v-menu
                                        v-model="menuEndDate"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >
                                        <template v-slot:activator="{ on }">

                                            <v-text-field
                                                label="End Date"
                                                v-model="endDate"
                                                readonly
                                                v-on="on"
                                                :rules="[rules.required]"
                                                :disabled="disabled"
                                            />

                                        </template>

                                        <v-date-picker v-model="endDate" :allowed-dates="allowedDatesForEndDate" />

                                    </v-menu>
                                </v-flex>
                            </template>
                        </v-layout>

                        <!-- TYPE -->
                        <v-layout row>
                            <v-flex xs12>
                                <v-autocomplete
                                    label="Type"
                                    v-model="timeOff.leaveTypeId"
                                    :disabled="disabled || !leavesBalancesLoaded"
                                    :items="leavesAllocation"
                                    :rules="[rules.required]"
                                    chips
                                    item-text="leaveName"
                                    item-value="leaveTypeId"
                                    @change="validateHours"
                                >

                                    <template v-slot:selection="data">
                                        <v-chip
                                            :selected="data.selected"
                                            class="chip--select-multi ml-0"
                                        >
                                            {{ data.item.leaveName }}
                                        </v-chip>
                                    </template>
                                </v-autocomplete>
                            </v-flex>
                        </v-layout>

                        <!-- HOURS -->
                        <v-layout row>
                            <v-flex xs12>
                                <v-text-field
                                    :label="enableDateRange ? 'Hours per day' : 'Hours'"
                                    v-model="timeOff.hours"
                                    :rules="[rules.required, rules.valid_hours]"
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    hint="In decimal format. (1.5 = 1hour 30mins)"
                                    persistent-hint
                                />
                            </v-flex>
                        </v-layout>

                        <!-- INCLUDE WEEKENDS -->
                        <v-layout row wrap v-if="enableDateRange">
                            <v-flex xs12 sm6>
                                <v-switch
                                    v-model="includeWeekends"
                                    label="Include weekends"
                                    color="primary"
                                />
                            </v-flex>
                            <v-flex xs12 sm6>
                                <div :class="isMobile?'':'mt-21'"><b>Total Hours: {{ totalRequestedHours }}</b></div>
                                <div></div>
                            </v-flex>
                        </v-layout>

                        <!-- REMARKS -->
                        <v-layout row>
                            <v-flex xs12>
                                <v-textarea
                                    v-model="timeOff.remarks"
                                    :rules="[rules.required]"
                                    maxlength="150"
                                    hint="150 characters max"
                                    label="Remarks"
                                    rows="1"
                                />

                            </v-flex>
                        </v-layout>

                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="mr-25">
                <v-spacer></v-spacer>
                <v-btn color="primary" type="submit" :loading="loading" :disabled="disabled || !validInputHours || isTimesheetApproved">
                    {{ timeOff.mode == 'Edit' ? 'Update' : 'Save' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script>

import teamsApi from '@/api/team'
import timeOffsApi from '@/api/timeoffs'
import leavesAllocationApi from '@/api/leavesallocation'
import timesheetApi from '@/api/timesheet'
import commonfn from '@/common/functions'   //for refactor
import awsS3Helper from '@/helpers/aws-s3'

export default {
    props: {
        enableDateRange: {
            type: Boolean,
            default: false
        }
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink')
    },
    data() {
        return {
            commonfn,   //for refactor
            timeOff: {},
            transDate: null,
            endDate: null,
            loading: false,
            loading_team_members: false,
            disabled: false,
            error: false,
            errorMessage: '',
            validInputHours: true,
            totalRequestedHours: 0,
            includeWeekends: false,
            leavesBalancesLoaded: false,
            leavesAllocation: [],
            cutoff: null,
            menuTransDate: false,
            menuEndDate: false,
            isRequestForOtherUser: false,
            requestForUserId: null,
            team_members: [],
            timereports: [],
            selectedCutOffId: null,
            mode: null,

            // put this in rules-helper.js
            rules: {
                required: value => !!value || 'Required.',
                valid_hours: value => value > 0 || 'Should be greater than zero',
                timesheetNotApproved: value => !this.isTimesheetApproved || 'The timesheet for this employee is already approved.'
            }
        }
    },
    methods: {
        async getAvailableBalanceBySelectedUser() {
            this.timeOff.leaveTypeId = null
            if (!this.isTimesheetApproved && this.requestForUserId) {
                await this.getAvailableLeaves()
                this.updateTotalHours()
            }
        },
        async getTimeReportsByTeam() {
            try {
                this.loading = true
                const response = await timesheetApi.getTimeReportByTeam(this.selectedCutOffId)
                if (response && response.status == 200) {
                    this.timereports = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getMembersBySelectedTeam() {
            try {
                this.loading_team_members = true
                const selectedTeamId = this.$store.state.selectedTeamId
                const response = await teamsApi.getMembersBySelectedTeam(selectedTeamId)
                if (response && response.status == 200) {
                    if (response.data.teamMembers && response.data.teamMembers.length) {
                        this.team_members = response.data.teamMembers.map(x => ({
                            userId: x.userId,
                            name: `${x.firstName} ${x.lastName}`,
                            avatar: awsS3Helper.getAvatarUrl(x.userId)
                        }));
                    }
                }
                this.loading_team_members = false
            }catch(error) {
                this.errorMessage = error
                this.error = true
            }
        },
        async setParams(data) {            
            this.timeOff = data
            this.cutoff = data.cutoff || null
            this.mode = data.mode

            if (data.mode == 'Edit') {
                // edit mode            
                this.transDate = this.$moment(data.transDate).format('YYYY-MM-DD')
                this.endDate = data.endDate
                        ? this.$moment(data.endDate).format('YYYY-MM-DD')
                        : this.transDate
                this.timeOff.leaveTypeId = data.type
                this.timeOff.hours = data.hours
                this.timeOff.remarks = data.remarks
                this.timeOff.timeOffId = data.timeOffId
                this.requestForUserId = data.userId
                this.isRequestForOtherUser = data.isRequestForOtherUser

                await this.getAvailableLeaves()
                this.updateTotalHours()
            } else {
                // add new mode
                this.transDate = this.getTransDate(data)
                this.endDate = this.transDate
                this.isRequestForOtherUser = data.isRequestForOtherUser
                this.selectedCutOffId = data.selectedCutOffId

                if (this.isRequestForOtherUser) {
                    //always pull latest since team can already be changed from anyware in team pages
                    await this.getMembersBySelectedTeam() 
                    await this.getTimeReportsByTeam()
                }else{
                    await this.getAvailableLeaves()
                    this.updateTotalHours()
                }
            }
        },
        getTransDate(data) {
            const today = this.$moment().startOf('day');

            if (this.cutoff && this.cutoff.cutoffStart && this.cutoff.cutoffEnd) {
                const start = this.$moment(this.cutoff.cutoffStart);
                const end = this.$moment(this.cutoff.cutoffEnd);

                // if today is within cutoff range, use today, else use cutoffStart
                return today.isBetween(start, end, null, '[]')
                    ? today.format('YYYY-MM-DD')
                    : start.format('YYYY-MM-DD');
            }

            // fallback: use provided transDate
            return this.$moment(data.transDate).format('YYYY-MM-DD');
        },
        allowedDates(val) {

            if (!this.cutoff)
                return true

            return (
                this.$moment(this.cutoff.cutoffStart)
                    .format('YYYY-MM-DD')
                <=
                this.$moment(val)
                    .format('YYYY-MM-DD')
                &&
                this.$moment(this.cutoff.cutoffEnd)
                    .format('YYYY-MM-DD')
                >=
                this.$moment(val)
                    .format('YYYY-MM-DD')
            )
        },
        allowedDatesForEndDate(val) {
            return (
                this.$moment(val)
                    .isSameOrAfter(this.transDate, 'day')
                &&
                this.allowedDates(val)
            )
        },
        onTransDateChange() {
            this.menuTransDate = false
            this.endDate = this.transDate
            this.getAvailableLeaves()
            this.updateTotalHours()
        },
        onEndDateSelected() {
            this.menuEndDate = false
            this.updateTotalHours()
        },
        async getAvailableLeaves() {

            this.leavesBalancesLoaded = false
            this.disabled = true

            try {

                this.leavesAllocation = []
                let response

                if (this.isRequestForOtherUser && this.requestForUserId) {
                    response = await leavesAllocationApi.getAvailableByUser(this.requestForUserId, this.transDate)
                }else{
                    response = await leavesAllocationApi.getAvailable(this.transDate)
                }
                

                if (response && response.status == 200) {

                    response.data.items.forEach(item => {
                        var balanceHours = item.earnedHours > item.balanceHours
                                        ? item.balanceHours
                                        : item.earnedHours

                        this.leavesAllocation.push({
                            leaveTypeId: item.leaveTypeId,
                            balanceHours: balanceHours,
                            leaveName: item.earnedHours == -1
                                    ? item.leaveName
                                    : `${item.leaveName} (${balanceHours} hrs available)`
                        })
                    })

                    this.leavesBalancesLoaded = true
                }

            } catch (error) {
                this.error = true
                this.errorMessage = error
            } finally {
                this.disabled = false
            }
        },
        formatHours() {
            if (!this.timeOff.hours)
                return

            if (parseFloat(this.timeOff.hours) > 8)
                this.timeOff.hours = 8

            this.timeOff.hours = parseFloat(this.timeOff.hours).toFixed(2)

            this.updateTotalHours()
        },
        updateTotalHours() {
            this.validInputHours = true

            if (!this.timeOff.hours)
                return

            var dailyHours =
                parseFloat(this.timeOff.hours)

            if (!this.enableDateRange) {
                this.totalRequestedHours = dailyHours
            } else {
                if (!this.transDate || !this.endDate)
                    return

                let total = 0
                let current = this.$moment(this.transDate)
                const end = this.$moment(this.endDate)

                while (current.isSameOrBefore(end, 'day')) {

                    const day = current.day()

                    if (this.includeWeekends || (day !== 0 && day !== 6)) {
                        total += dailyHours
                    }

                    current.add(1, 'day')
                }

                this.totalRequestedHours = total
            }

            this.totalRequestedHours = parseFloat(this.totalRequestedHours).toFixed(2)

            this.validateHours()
        },
        validateHours() {
            this.validInputHours = true

            const selected = this.leavesAllocation.find(x => {
                return x.leaveTypeId == this.timeOff.leaveTypeId
            })

            if (!selected)
                return

            if (selected.balanceHours == -1)
                return

            if (parseFloat(this.totalRequestedHours) > parseFloat(selected.balanceHours)) {
                this.validInputHours = false
            }
        },
        async upsertTimeOff() {
            if (!this.$refs.form.validate() || !this.validInputHours || !this.timeOff.leaveTypeId) {
                return
            }

            try {
                this.loading = true
                this.disabled = true

                const payload = {
                    timeOffId: this.timeOff.timeOffId || 0,
                    transDate: this.transDate,
                    endDate: this.enableDateRange
                            ? this.endDate
                            : this.transDate,
                    hours: this.timeOff.hours,
                    type: this.timeOff.leaveTypeId,
                    includeWeekends: this.includeWeekends,
                    isRequestForOtherUser: this.isRequestForOtherUser,
                    requestForUserId: this.requestForUserId,
                    remarks: this.timeOff.remarks
                }

                let response = null

                if (this.timeOff.mode == 'Edit')
                    response = await timeOffsApi.update(payload)
                else
                    response = await timeOffsApi.add(payload)

                if (response && response.status == 200) {
                    this.closeDialog()
                    this.$emit('send', null)
                }

            } catch (error) {
                this.error = true
                this.errorMessage = error
            } finally {
                this.loading = false
                this.disabled = false
            }
        },
        clearForm() {
            this.error = false
            this.errorMessage = ''

            this.loading = false
            this.disabled = false

            this.validInputHours = true
            this.totalRequestedHours = 0
            this.leavesBalancesLoaded = false
            this.includeWeekends = false
        },
        closeDialog() {
            this.$emit('update:dialogPTO', false)
        }
    },
    watch: {
        transDate() {
            this.onTransDateChange()
        },
        endDate() {
            this.menuEndDate = false
            this.updateTotalHours()
        },
        includeWeekends() {
            this.updateTotalHours()
        },
        'timeOff.hours'() {
            this.formatHours()
        }
    },
    computed: {
        isApproved() {
            return this.timeOff && this.timeOff.status == commonfn.getStatusCode('Approved')
        },
        isTimesheetApproved() {
            if (this.requestForUserId && this.timereports && this.timereports.length) {
                const timereport = this.timereports.find(x => x.userId == this.requestForUserId)
                return timereport && timereport.status == commonfn.getStatusCode('Approved')
            }
            return false
        }
    },
}
</script>

<style scoped>

.form-content {
    padding:0px 25px 0px 25px;
}

</style>